import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as IO from '@karve.it/interfaces/artifacts';
import { ListArtifactInput, ListArtifactOutput } from '@karve.it/interfaces/artifacts';
import { Apollo } from 'apollo-angular';

import { forkJoin, map, switchMap } from 'rxjs';

import { FreyaFile } from '../../shared/upload-file/upload-file.component';

@Injectable({
  providedIn: 'root'
})
export class ArtifactService {

  constructor(
    private apollo: Apollo,
    private httpClient: HttpClient,
  ) { }

  /**
   * Create new artifacts and upload them to google bucket.
   *
   * @param artifacts Array of artifacts to be created.
   * @returns QueryRef, Total number created, and the created objects.
   */
  createArtifact(artifacts: IO.ArtifactInput[]) {
    return this.apollo.mutate<IO.CreateArtifactOutput>({
      mutation: IO.createArtifacts,
      variables: { artifacts },
    });
  }

  /**
   * Gets a list of all artifacts that match the input.
   *
   * @param input Artifact input
   * @returns QueryRef, List of artifacts that match the fitler.
   */
  listArtifacts(input: ListArtifactInput) {
    return this.apollo.query<ListArtifactOutput>({
      query: IO.viewArtifacts,
      variables: { ...input },
    });
  }

  /**
   * Gets a list of all artifacts that match the input.
   *
   * @param input Artifact input
   * @returns QueryRef, List of artifacts that match the fitler.
   */
  watchArtifacts(input: ListArtifactInput) {
    return this.apollo.watchQuery<ListArtifactOutput>({
      query: IO.viewArtifacts,
      variables: { ...input },
    });
  }

  /**
   * List how much sapce has been used by the system and system users.
   *
   * @param input Filter which/whose usage you want to list.
   * @returns QueryRef, Usage of the system and users by uploading/interacting with artifacts.
   */
  listArtifactUsage(input: IO.ArtifactUsageInput) {
    return this.apollo.watchQuery<IO.ArtifactsUsageOutput>({
      query: IO.artifactUsageQuery,
      variables: {
        ...input
      }
    });
  }

  /**
   * Delete artifacts from the system
   *
   * @param artifactIds Ids of the artifacts you want to delete.
   * @returns True if the operation was successful, false if it failed.
   */
  deleteArtifacts(artifactIds: string[]) {
    return this.apollo.mutate<IO.DeleteArtifactOutput>({
      mutation: IO.deleteArtifacts,
      variables: {
        artifactIds,
      }
    }).pipe(
      // console.log(result);
      // @ts-ignore
      map(res => res.data.deleteArtifacts)
    );
  }

  uploadCommentArtifacts(files: File[]) {
    return this.createArtifact(files.map((file) => ({
      contentType: file.type,
      fileSize: file.size,
      name: file.name,
      namespace: 'job-comment-artifact',
      attributes: [`lastModified:${file.lastModified}`],
    }))).pipe(
      map((results) => results.data.createArtifacts.artifacts.map((artifact) => {

        if (!artifact.signedPolicy) {
          throw new Error(`Failed to retrieve upload policy for ${artifact.name}`);
        }

        let signedPolicy: { fields: { [key: string ]: any }; url: string };

        try {
          signedPolicy = JSON.parse(artifact.signedPolicy);
        } catch (_error) {
          throw new Error(`Invalid upload policy for ${artifact.name}`);
        }

        return ({ artifact, signedPolicy });
      })),
      switchMap((artifacts) => {

        const uploadFiles$ = artifacts
          .map((
            { artifact, signedPolicy },
            i,
          ) => this.uploadFile(files[i], signedPolicy).pipe(
            map(() => artifact),
          ));

        return forkJoin(uploadFiles$);
      }),
    );
  }

  uploadFile(file: FreyaFile, policy: { fields: { [key: string ]: any }; url: string }) {
    const formData = new FormData();

    for (const f of Object.keys(policy.fields)) {
      const value = policy.fields[f];
      formData.append(f, value);
    }

    formData.append('content-type', file.type);
    formData.append('file', file);

    return this.httpClient.post(policy.url, formData, { headers: { 'ngsw-bypass': 'true' } });
  }

  // Not Functional
  // async uploadArtifactFile(policyString: string, file: Buffer, contentType: string) {

  //   const signedPolicy = JSON.parse(policyString);

  //   const formData = {
  //       file,
  //       ...signedPolicy.fields,
  //       'content-type': contentType,
  //   };
  //   await this.http.post(signedPolicy.url, formData);

  //   await request.post({
  //     url: signedPolicy.url,
  //     formData,
  // });
  // }
}
